import {TrackingPageTypeWithoutId} from '@aboutyou/legacy-tracking-sdk';
import React, {FC} from 'react';
import styled from 'styled-components';
import {HelpCenterEndpoint} from '../../../pages/HelpCenter/helpCenterEndpoint';
import {
  HelpCenterLandingPageData,
  HelpCenterPageCommonProps,
} from '../../../pages/HelpCenter/types';
import {SharedPageTrackingContextProvider} from '../../../tracking/context/PageTrackingContext';
import {getPageContextProps} from '../../../tracking/hooks/trackingHelpers';
import {backgroundColor, complementaryColor} from '../../constants/colors';
import {mediaMinTablet} from '../../constants/customMedia';
import HiddenSeoH1 from '../../seo/HiddenSeoH1';
import HelpdeskFooter from '../common/HelpdeskFooter';
import PageWrapper, {PaddedPageSection} from '../common/PageWrapper';
import {DesktopFrequentQuestions} from './DesktopFrequentQuestions';
import HelpSectionLinks from './HelpSectionLinks';
import HelpdeskLandingPageHeader from './HelpdeskLandingPageHeader';
import MobileFrequentQuestions from './MobileFrequentQuestions';
import {SelfServiceFlowPanel} from './SelfServiceFlowPanel';

const FullWidthWrapper = styled.div`
  width: 100%;
  background-color: ${complementaryColor};
`;
const FullWidthGrayWrapper = styled.div`
  width: 100%;
  background-color: ${backgroundColor};
`;

const SSFPanelContainer = styled.div`
  margin-bottom: 40px;
  margin-top: -40px;
  padding: 0 20px;

  @media ${mediaMinTablet} {
    margin-top: -60px;
    margin-bottom: -20px;
    transform: translateY(-50%);
  }
`;

const HelpdeskLandingPageUI: FC<{
  isShowOldHelpCenterEnabled: boolean;
  locale: HelpCenterPageCommonProps['locale'];
  translations: HelpCenterPageCommonProps['translations'];
  sections: HelpCenterPageCommonProps['sections'];
  highlightQuestions: HelpCenterLandingPageData['highlightQuestions'];
  frequentQuestions: HelpCenterLandingPageData['highlightArticles'];
  headerImageLarge: HelpCenterLandingPageData['headerImageLarge'];
  headerImageSmall: HelpCenterLandingPageData['headerImageSmall'];
  endpoints: HelpCenterEndpoint;
  isMobileHeaderShown?: boolean;
  showLeftArrow?: boolean;
}> = ({
  isShowOldHelpCenterEnabled,
  locale,
  translations,
  frequentQuestions,
  highlightQuestions,
  sections,
  headerImageLarge,
  headerImageSmall,
  endpoints,
  isMobileHeaderShown = true,
  showLeftArrow = false,
}) => {
  const pageContext = getPageContextProps(TrackingPageTypeWithoutId.HelpCenter);
  return (
    <SharedPageTrackingContextProvider
      {...pageContext}
      type={TrackingPageTypeWithoutId.HelpCenter}
    >
      <FullWidthWrapper>
        <HiddenSeoH1>{translations.landingPage.seo_page_h1}</HiddenSeoH1>
        <HelpdeskLandingPageHeader
          locale={locale}
          title={translations.landingPage.page_title}
          subTitle={translations.landingPage.page_sub_title}
          highlightQuestions={highlightQuestions}
          sections={sections}
          headerImageLarge={headerImageLarge}
          headerImageSmall={headerImageSmall}
          translations={translations}
          endpoints={endpoints}
          isMobileHeaderShown={isMobileHeaderShown}
          showLeftArrow={showLeftArrow}
        />
        <FullWidthGrayWrapper>
          <PageWrapper>
            <PaddedPageSection style={{alignItems: 'center'}}>
              {!isShowOldHelpCenterEnabled && (
                <SSFPanelContainer>
                  <SelfServiceFlowPanel
                    title={
                      translations.landingPage.self_service_flow.panel_title
                    }
                    cta={translations.landingPage.self_service_flow.cta}
                  />
                </SSFPanelContainer>
              )}

              <DesktopFrequentQuestions
                title={translations.landingPage.faq}
                frequentQuestions={frequentQuestions}
              />
              <MobileFrequentQuestions
                title={translations.landingPage.faq}
                frequentQuestions={frequentQuestions}
              />
              <HelpSectionLinks
                translations={translations.landingPage}
                highlightQuestions={highlightQuestions}
                title={translations.landingPage.help_sections_heading}
                suggestionsProps={{
                  locale,
                  translations,
                  endpoints,
                  sections,
                  isMobileHeaderShown,
                  showLeftArrow,
                }}
              />
            </PaddedPageSection>
          </PageWrapper>
        </FullWidthGrayWrapper>
        <PageWrapper>
          <HelpdeskFooter
            translations={translations.footer}
            isShowOldHelpCenterEnabled={isShowOldHelpCenterEnabled}
          />
        </PageWrapper>
      </FullWidthWrapper>
    </SharedPageTrackingContextProvider>
  );
};

export default HelpdeskLandingPageUI;
